import React, { useState } from 'react';
import type { Account } from '@axiom/validation';
import {
  Form,
  Modal,
  ModalFooter,
  ModalHeader,
  ModalSection,
  Dropdown,
  Button,
  ParagraphHeader,
  Paragraph,
  Gutter,
  Banner,
} from '@axiom/ui';

import { EnvUtil } from '../../utils/env-util';

import { ImpersonationClientPickerModalFormSchema } from './impersonation-client-picker-modal-form-schema';

export type ImpersonationClientPickerModalProps = {
  account: Account;
  onClose: () => void;
  selectContact: (prop: Account['contacts'][number]) => Promise<void> | void;
  submitButtonCopy: string;
};

export const ImpersonationClientPickerModal = ({
  account,
  onClose,
  selectContact,
  submitButtonCopy,
}: ImpersonationClientPickerModalProps) => {
  const [selectedContact, setSelectedContact] =
    useState<Account['contacts'][number]>();

  const showErrorBanner = account.contacts.length === 0;

  const handleOnSubmit = async () => {
    await selectContact(selectedContact as Account['contacts'][number]);
  };

  const handleBannerLinkClick = (e: React.MouseEvent) => {
    e.preventDefault();

    window.open(EnvUtil.envoyUrl, '_blank');
    onClose();
  };

  return (
    <Form
      name="IMPERSONATION_CLIENT_PICKER_FORM"
      schema={ImpersonationClientPickerModalFormSchema}
      initialValues={{
        CONTACT_ID: '',
      }}
      onSubmit={handleOnSubmit}
    >
      {({ fireSubmit }) => {
        return (
          <Modal name="IMPERSONATION_CLIENT_PICKER_MODAL" size="confirm">
            <ModalHeader
              name="MODAL_HEADER"
              onClose={() => {
                onClose();
              }}
            >
              Provide account details
            </ModalHeader>
            <ModalSection disableScrolling>
              {showErrorBanner ? (
                <Gutter bottom="24px">
                  <Banner name="NO_CONTACTS_BANNER" type="error" impact="high">
                    No Contacts exist for this account.
                    <br />
                    Please{' '}
                    <a
                      data-test="BANNER_LINK"
                      href={EnvUtil.envoyUrl}
                      onClick={handleBannerLinkClick}
                    >
                      add a contact to this account in AHQ
                    </a>
                  </Banner>
                </Gutter>
              ) : (
                <div data-test="PICK_CONTACT">
                  {account.calculatedName ? (
                    <>
                      <ParagraphHeader name="HEADER">Account</ParagraphHeader>
                      <Paragraph name="ACCOUNT_NAME">
                        {account.calculatedName}
                      </Paragraph>
                      <Gutter bottom="24px" />
                    </>
                  ) : null}

                  <Dropdown
                    label="Contact"
                    description="Search for the user you would like to make the primary contact on this engagement."
                    name="CONTACT_ID"
                    options={account.contacts.map(contact => ({
                      ...contact,
                      fullName: `${contact.firstName} ${contact.lastName}`,
                    }))}
                    displayKey="fullName"
                    valueKey="id"
                    onChange={(contactId, e, contact) => {
                      const foundContact =
                        contact as Account['contacts'][number];
                      setSelectedContact(foundContact);
                    }}
                  />
                </div>
              )}
            </ModalSection>
            <ModalFooter>
              <Button
                name="PICK_CLIENT_CANCEL_BUTTON"
                variation="outline"
                type="button"
                onClick={() => {
                  onClose();
                }}
              >
                Cancel
              </Button>
              {showErrorBanner ? null : (
                <Button
                  name="PICK_CLIENT_NEXT_BUTTON"
                  type="submit"
                  onClick={async () => fireSubmit()}
                >
                  {submitButtonCopy}
                </Button>
              )}
            </ModalFooter>
          </Modal>
        );
      }}
    </Form>
  );
};
